import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Box,
  Alert,
  LinearProgress,
  Paper,
  Divider,
  useTheme,
  useMediaQuery,
  Fade
} from '@mui/material';
import {
  Business,
  Email,
  Phone,
  HourglassTop,
  Send,
  SkipNext
} from '@mui/icons-material';
import { CustomerFormData } from './CustomerForm';

interface AnalysisWaitingFormProps {
  open: boolean;
  uploadId: string;
  analyzing: boolean;
  onSubmit: (data: CustomerFormData) => Promise<void>;
  onSkip: () => void;
  submitting?: boolean;
}

const AnalysisWaitingForm: React.FC<AnalysisWaitingFormProps> = ({
  open,
  uploadId,
  analyzing,
  onSubmit,
  onSkip,
  submitting = false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [formData, setFormData] = useState<CustomerFormData>({
    upload_id: uploadId,
    company_name: '',
    contact_email: '',
    contact_phone: '',
    industry: '',
    customer_type: '',
    quantity_category: '',
    product_type: '',
    how_found: '',
    notes: '',
    consent_given: false
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Update upload_id when it changes
  React.useEffect(() => {
    if (uploadId) {
      setFormData(prev => ({ ...prev, upload_id: uploadId }));
    }
  }, [uploadId]);

  const handleChange = (field: keyof CustomerFormData) => (
    event: React.ChangeEvent<HTMLInputElement | { value: unknown }>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, consent_given: event.target.checked }));
    if (errors.consent_given) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.consent_given;
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.company_name.trim()) {
      newErrors.company_name = 'Firmenname ist erforderlich';
    }

    if (!formData.contact_email.trim()) {
      newErrors.contact_email = 'E-Mail ist erforderlich';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.contact_email)) {
      newErrors.contact_email = 'Ungültige E-Mail-Adresse';
    }

    if (!formData.consent_given) {
      newErrors.consent_given = 'Datenschutz-Einwilligung ist erforderlich';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    // Debug: Log form data before submission
    console.log('Submitting lead with data:', formData);

    if (!formData.upload_id) {
      alert('Fehler: Upload-ID fehlt. Bitte laden Sie die Datei erneut hoch.');
      return;
    }

    await onSubmit(formData);
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          background: theme.palette.mode === 'dark' 
            ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
            : 'linear-gradient(135deg, #ffffff 0%, #f5f7fa 100%)',
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <HourglassTop sx={{ fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant="h5" fontWeight={600}>
              Nutzen Sie die Wartezeit
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Während wir Ihre Daten analysieren, können Sie bereits Ihre Kontaktdaten hinterlegen
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        {/* Analysis Progress Indicator */}
        {analyzing && (
          <Fade in={analyzing}>
            <Alert 
              severity="info" 
              icon={<HourglassTop />}
              sx={{ mb: 3 }}
            >
              <Typography variant="body2" fontWeight={600} gutterBottom>
                Analyse läuft im Hintergrund...
              </Typography>
              <LinearProgress sx={{ mt: 1 }} />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Die Ergebnisse werden gleich angezeigt, sobald Sie fertig sind!
              </Typography>
            </Alert>
          </Fade>
        )}

        <Typography variant="h6" gutterBottom fontWeight={600} sx={{ mb: 2 }}>
          Ihre Kontaktdaten
        </Typography>

        <Grid container spacing={2}>
          {/* Company Name */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Firmenname *"
              value={formData.company_name}
              onChange={handleChange('company_name')}
              error={!!errors.company_name}
              helperText={errors.company_name}
              InputProps={{
                startAdornment: <Business sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>

          {/* Email */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="email"
              label="E-Mail *"
              value={formData.contact_email}
              onChange={handleChange('contact_email')}
              error={!!errors.contact_email}
              helperText={errors.contact_email}
              InputProps={{
                startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>

          {/* Phone */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="tel"
              label="Telefon (optional)"
              value={formData.contact_phone}
              onChange={handleChange('contact_phone')}
              InputProps={{
                startAdornment: <Phone sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>

          {/* Industry */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Branche (optional)</InputLabel>
              <Select
                value={formData.industry}
                onChange={handleChange('industry') as any}
                label="Branche (optional)"
              >
                <MenuItem value=""><em>Bitte wählen</em></MenuItem>
                <MenuItem value="Manufacturing">Produktion/Fertigung</MenuItem>
                <MenuItem value="Automotive">Automotive</MenuItem>
                <MenuItem value="Energy">Energie</MenuItem>
                <MenuItem value="Technology">Technologie/IT</MenuItem>
                <MenuItem value="Healthcare">Gesundheitswesen</MenuItem>
                <MenuItem value="Industrial">Industrie</MenuItem>
                <MenuItem value="Aerospace">Luft- und Raumfahrt</MenuItem>
                <MenuItem value="Marine">Marine/Schifffahrt</MenuItem>
                <MenuItem value="Telecommunications">Telekommunikation</MenuItem>
                <MenuItem value="Other">Sonstige</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Customer Type */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Kundentyp (optional)</InputLabel>
              <Select
                value={formData.customer_type}
                onChange={handleChange('customer_type') as any}
                label="Kundentyp (optional)"
              >
                <MenuItem value=""><em>Bitte wählen</em></MenuItem>
                <MenuItem value="B2B">B2B (Geschäftskunde)</MenuItem>
                <MenuItem value="B2C">B2C (Endkunde)</MenuItem>
                <MenuItem value="Public">Öffentliche Einrichtung</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* How Found */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Wie haben Sie uns gefunden? (optional)</InputLabel>
              <Select
                value={formData.how_found}
                onChange={handleChange('how_found') as any}
                label="Wie haben Sie uns gefunden? (optional)"
              >
                <MenuItem value=""><em>Bitte wählen</em></MenuItem>
                <MenuItem value="Google Search">Suchmaschine (Google)</MenuItem>
                <MenuItem value="LinkedIn">LinkedIn</MenuItem>
                <MenuItem value="Referral">Empfehlung</MenuItem>
                <MenuItem value="Trade Show">Messe/Event</MenuItem>
                <MenuItem value="Website">Website direkt</MenuItem>
                <MenuItem value="Social Media">Social Media</MenuItem>
                <MenuItem value="Advertisement">Werbung</MenuItem>
                <MenuItem value="Partner">Partner</MenuItem>
                <MenuItem value="Other">Sonstige</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Notes */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Anmerkungen (optional)"
              value={formData.notes}
              onChange={handleChange('notes')}
              placeholder="Haben Sie spezielle Anforderungen oder Fragen?"
            />
          </Grid>

          {/* Consent */}
          <Grid item xs={12}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderColor: errors.consent_given ? 'error.main' : 'divider'
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.consent_given}
                    onChange={handleCheckboxChange}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    Ich stimme der Verarbeitung meiner Daten gemäß der{' '}
                    <a href="/datenschutz" target="_blank" rel="noopener noreferrer">
                      Datenschutzerklärung
                    </a>{' '}
                    zu. *
                  </Typography>
                }
              />
              {errors.consent_given && (
                <Typography variant="caption" color="error" sx={{ ml: 4, display: 'block', mt: 1 }}>
                  {errors.consent_given}
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2.5, gap: 1 }}>
        <Button
          onClick={onSkip}
          disabled={submitting}
          startIcon={<SkipNext />}
          sx={{ mr: 'auto' }}
        >
          Später ausfüllen
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={submitting ? undefined : <Send />}
          sx={{ minWidth: 120 }}
        >
          {submitting ? 'Wird gesendet...' : 'Absenden'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AnalysisWaitingForm;
